/**
 * Zwei Clients, klar getrennt:
 *
 * - `BrowserHubClient` laeuft im Kunden-Admin und spricht **nur** mit der
 *   lokalen Proxy-Route. Er kennt den Site-Key nicht und darf ihn nie kennen.
 * - `EngineClient` laeuft server-seitig und spricht mit der Engine.
 *
 * Die Trennung ist der Kern des Sicherheitsmodells: Ein kompromittiertes
 * Admin-Frontend kann nichts, was der Proxy nicht erlaubt.
 */

import type {
  HubArticle,
  HubArticleDetail,
  HubIdee,
  HubIdeenEinstellungen,
  HubIdeenTelegram,
  HubIdeenTelegramLink,
  HubVorschlaege,
  HubVorschlag,
  HubIdeeStatus,
  HubMedium,
  HubMediumAnlegen,
  HubMediumNeu,
  HubSite,
  HubAktionNeu,
  HubSocialFreigabe,
  HubSocialKalender,
  HubSocialPostNeu,
  HubTextVorschlag,
  HubTextVorschlagAnfrage,
  HubSocialKonto,
  HubKommentare,
  HubTikTokCreator,
  HubSocialPlan,
  HubSocialPost,
  HubSocialPostAendern,
  HubNewsletterAktion,
  HubNewsletterAusgabe,
  HubNewsletterAusgabeAendern,
  HubNewsletterThema,
  HubNewsletterThemaIn,
  HubNewsletterAbonnent,
  HubNewsletterEntwurf,
  HubNewsletterLokal,
  HubMeineTickets,
  HubTicket,
  HubTicketErgebnis,
} from "./types.js";

export class HubError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** `detail` der Engine, wenn sie mehr als einen Satz schickt — etwa `{code, message, befunde}` (ab 0.15.0). */
    readonly detail: unknown = null,
  ) {
    super(message);
    this.name = "HubError";
  }

  /** Maschinenlesbarer Grund, z.B. `blocked_quota` oder `hwg`. */
  get code(): string {
    const d = this.detail as { code?: unknown } | null;
    return d && typeof d === "object" && typeof d.code === "string" ? d.code : "";
  }

  /** Befunde einer abgelehnten HWG-Pruefung. */
  get befunde(): string[] {
    const d = this.detail as { befunde?: unknown } | null;
    return d && typeof d === "object" && Array.isArray(d.befunde) ? d.befunde.map(String) : [];
  }
}

async function parse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    let detail: unknown = null;
    let text = body.slice(0, 500) || res.statusText;
    try {
      detail = (JSON.parse(body) as { detail?: unknown }).detail ?? null;
      if (typeof detail === "string") text = detail;
      else if (detail && typeof detail === "object" && "message" in detail) text = String((detail as { message: unknown }).message);
    } catch {
      /* Klartext */
    }
    throw new HubError(text, res.status, detail);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * Wie der Browser-Client die eigene Proxy-Route erreicht.
 *
 * Ein Admin mit Session-Cookie (Next + NextAuth) braucht nichts davon. Eine SPA,
 * die ihr Backend per `Authorization: Bearer …` anspricht, gibt hier ihren
 * Header mit — sonst kommt jede Anfrage beim Proxy unangemeldet an.
 */
export interface HubClientOptions {
  /** Zusaetzliche Header je Anfrage, z.B. das Token des Admins. Wird bei jeder Anfrage neu gelesen. */
  headers?: () => RequestInit["headers"] | Promise<RequestInit["headers"]>;
  /** Standard `same-origin`; `include`, wenn die Proxy-Route auf einer anderen Domain liegt. */
  credentials?: RequestInit["credentials"];
  /**
   * Was zwischen `basePath` und dem Routenpfad steht, Standard `/hub` (die
   * Proxy-Route der Website). Das Portal spricht die Engine direkt an —
   * `basePath` ist dort `…/api/v1/portal/sites/{id}` und `hubPath` leer
   * (ab 0.14.0, docs/PORTAL.md).
   */
  hubPath?: string;
}
// Bewusst RequestInit[...] statt HeadersInit/RequestCredentials: Die beiden sind
// reine DOM-Typen. core/ wird auch server-seitig (Node/Hono ohne "DOM" in lib)
// importiert, und dort kennt @types/node nur RequestInit (Hinweis aus der
// EW-Anbindung, 13.09.2026).

/** Im Browser des Kunden-Admins. `basePath` ist die eigene Proxy-Route. */
export class BrowserHubClient {
  constructor(
    private readonly basePath = "/api/content-hub",
    private readonly options: HubClientOptions = {},
  ) {}

  private url(path: string): string {
    return `${this.basePath.replace(/\/$/, "")}${this.options.hubPath ?? "/hub"}${path}`;
  }

  private async req(path: string, init: RequestInit = {}): Promise<Response> {
    const headers = new Headers(this.options.headers ? await this.options.headers() : undefined);
    new Headers(init.headers).forEach((value, key) => headers.set(key, value));
    return fetch(this.url(path), {
      ...init,
      headers,
      credentials: this.options.credentials ?? "same-origin",
    });
  }

  private post(path: string, body?: unknown): Promise<Response> {
    return this.req(path, {
      method: "POST",
      ...(body === undefined
        ? {}
        : { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
    });
  }

  site(): Promise<HubSite> {
    return this.req("/site").then(parse<HubSite>);
  }

  articles(status?: string): Promise<HubArticle[]> {
    const q = status ? `?status=${encodeURIComponent(status)}` : "";
    return this.req(`/articles${q}`).then(parse<HubArticle[]>);
  }

  article(id: number): Promise<HubArticleDetail> {
    return this.req(`/articles/${id}`).then(parse<HubArticleDetail>);
  }

  approve(id: number, approvedBy = ""): Promise<HubArticle> {
    return this.post(`/articles/${id}/approve`, { approved_by: approvedBy, via: "hub" }).then(parse<HubArticle>);
  }

  /**
   * Aenderungswunsch. `permanent` ist die eine Zusatzfrage aus dem Konzept:
   * "nur diesmal, oder immer?" — bei `true` wird daraus spaeter eine dauerhafte
   * Regel, statt dass der Kunde dieselbe Korrektur in zwei Monaten wiederholt.
   */
  requestChanges(id: number, feedback: string, permanent = false, requestedBy = ""): Promise<HubArticle> {
    return this.post(`/articles/${id}/changes`, { feedback, permanent, requested_by: requestedBy })
      .then(parse<HubArticle>);
  }

  /**
   * Beitrag verwerfen (ab 0.21.0) — er verlaesst die Freigabe und erscheint
   * nie, auch nicht nach Ablauf der Frist. Nur vor der Veroeffentlichung; ein
   * veroeffentlichter Beitrag wird auf der Website selbst geloescht (409).
   * Zaehlt weiter aufs Kontingent, wenn er schon geschrieben war.
   */
  discardArticle(id: number, reason = "", discardedBy = ""): Promise<HubArticle> {
    return this.post(`/articles/${id}/discard`, { reason, discarded_by: discardedBy })
      .then(parse<HubArticle>);
  }

  refreshPreview(id: number): Promise<HubArticle> {
    return this.post(`/articles/${id}/preview`).then(parse<HubArticle>);
  }

  setPaused(paused: boolean, reason = ""): Promise<{ paused: boolean; reason: string }> {
    const q = `?paused=${paused}&reason=${encodeURIComponent(reason)}`;
    return this.post(`/pause${q}`).then(parse<{ paused: boolean; reason: string }>);
  }

  /**
   * Eine Meldung aus dem Admin — Fehler, Idee, Wunsch (ab 0.12.0).
   *
   * Wer der Melder ist, setzt der **Proxy** aus der Session der Website. Kaeme
   * es aus dem Browser, koennte sich jeder als jeder ausgeben.
   */
  ticketMelden(ticket: HubTicket): Promise<HubTicketErgebnis> {
    return this.post("/tickets", ticket).then(parse<HubTicketErgebnis>);
  }

  /**
   * Der Stand der eigenen Meldungen (ab 0.13.0).
   *
   * Wer fragt, sagt der Proxy aus der Session — deshalb kein Parameter.
   */
  meineTickets(): Promise<HubMeineTickets> {
    return this.req("/tickets").then(parse<HubMeineTickets>);
  }

  /** Video-Ideen dieser Site (Ideen-Radar). Leer, wenn nicht gebucht. */
  ideen(): Promise<HubIdee[]> {
    return this.req("/ideen").then(parse<HubIdee[]>);
  }

  /** Umsetzen, ablehnen oder als umgesetzt melden — mit Link zum eigenen Beitrag. */
  decideIdee(id: number, status: Exclude<HubIdeeStatus, "proposed">, by = "", postUrl = ""): Promise<HubIdee> {
    return this.post(`/ideen/${id}`, { status, by, post_url: postUrl }).then(parse<HubIdee>);
  }

  /** Ausschluesse der Ideen (ab 0.20.0). */
  ideenEinstellungen(): Promise<HubIdeenEinstellungen> {
    return this.req("/ideen/einstellungen").then(parse<HubIdeenEinstellungen>);
  }

  ideenEinstellungenSpeichern(e: HubIdeenEinstellungen): Promise<HubIdeenEinstellungen> {
    return this.send("/ideen/einstellungen", "PUT", e).then(parse<HubIdeenEinstellungen>);
  }

  /** KI-Vorschlaege ohne Vorlage und ob gerade neue entstehen (ab 0.20.0). */
  vorschlaege(): Promise<HubVorschlaege> {
    return this.req("/ideen/vorschlaege").then(parse<HubVorschlaege>);
  }

  /** Zehn weitere Ideen anstossen — sie entstehen im Hintergrund (etwa eine Minute). */
  vorschlaegeAnfordern(by = ""): Promise<{ running: boolean }> {
    return this.post("/ideen/vorschlaege", { by }).then(parse<{ running: boolean }>);
  }

  decideVorschlag(id: number, status: Exclude<HubIdeeStatus, "proposed">, by = "", postUrl = ""): Promise<HubVorschlag> {
    return this.post(`/ideen/vorschlaege/${id}`, { status, by, post_url: postUrl }).then(parse<HubVorschlag>);
  }

  /** Telegram-Konten, die Videos fuer diese Site schicken (ab 0.19.0). */
  ideenTelegram(): Promise<HubIdeenTelegram[]> {
    return this.req("/ideen/telegram").then(parse<HubIdeenTelegram[]>);
  }

  /** Einmal-Link zum Verbinden — gilt sieben Tage, steht nur in dieser Antwort. */
  ideenTelegramVerbinden(by = ""): Promise<HubIdeenTelegramLink> {
    return this.post("/ideen/telegram", { by }).then(parse<HubIdeenTelegramLink>);
  }

  /** Verbindung trennen oder offenen Link zurueckziehen. Die Sammlung bleibt. */
  ideenTelegramTrennen(id: number): Promise<void> {
    return this.send(`/ideen/telegram/${id}`, "DELETE").then(parse<void>);
  }

  private send(path: string, method: string, body?: unknown): Promise<Response> {
    return this.req(path, {
      method,
      ...(body === undefined
        ? {}
        : { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
    });
  }

  /** Die Social-Mediathek dieser Site. 403, wenn Social nicht gebucht ist. */
  mediathek(): Promise<HubMedium[]> {
    return this.req("/mediathek").then(parse<HubMedium[]>);
  }

  /** Medium anlegen — gibt die signierten Upload-Links fuer Datei und Standbild. */
  mediumAnlegen(meta: HubMediumAnlegen): Promise<HubMediumNeu> {
    return this.post("/mediathek", meta).then(parse<HubMediumNeu>);
  }

  /** Nach dem Upload: pruefen lassen und die Beschreibung anstossen. */
  mediumFertig(id: number): Promise<HubMedium> {
    return this.post(`/mediathek/${id}/fertig`).then(parse<HubMedium>);
  }

  mediumAendern(id: number, patch: { approved_for_use?: boolean; title?: string; description?: string }): Promise<HubMedium> {
    return this.send(`/mediathek/${id}`, "PATCH", patch).then(parse<HubMedium>);
  }

  async mediumLoeschen(id: number): Promise<void> {
    const res = await this.send(`/mediathek/${id}`, "DELETE");
    if (!res.ok) await parse<unknown>(res);
  }

  /** Die letzten Social-Plaene mit ihren Beitraegen. 403, wenn Social nicht gebucht ist. */
  socialPlaene(): Promise<HubSocialPlan[]> {
    return this.req("/social/plaene").then(parse<HubSocialPlan[]>);
  }

  /** Text, Termin, Medien anpassen — oder freigeben / verwerfen. */
  socialPostAendern(id: number, patch: HubSocialPostAendern): Promise<HubSocialPost> {
    return this.send(`/social/posts/${id}`, "PATCH", patch).then(parse<HubSocialPost>);
  }

  /** Alle noch offenen Beitraege eines Plans freigeben. Was die HWG-Pruefung nicht besteht, bleibt offen. */
  socialPlanFreigeben(planId: number, by = ""): Promise<HubSocialFreigabe> {
    return this.post(`/social/plaene/${planId}/freigeben`, { by }).then(parse<HubSocialFreigabe>);
  }

  /** Alle Beitraege im Zeitraum — Monatsplan, Aktionen, eigene (ab 0.15.0). Datum als `YYYY-MM-DD`. */
  socialKalender(von?: string, bis?: string): Promise<HubSocialKalender> {
    const q = new URLSearchParams();
    if (von) q.set("von", von);
    if (bis) q.set("bis", bis);
    const query = q.toString();
    return this.req(`/social/beitraege${query ? `?${query}` : ""}`).then(parse<HubSocialKalender>);
  }

  /** Ein eigener Beitrag — ohne Modell, an jedem Tag (ab 0.15.0). */
  socialPostAnlegen(post: HubSocialPostNeu): Promise<HubSocialPost> {
    return this.post("/social/posts", post).then(parse<HubSocialPost>);
  }

  /** Nur eigene, noch nicht gepostete Beitraege. */
  socialPostLoeschen(id: number): Promise<void> {
    return this.send(`/social/posts/${id}`, "DELETE").then(parse<void>);
  }

  /** Caption und Hashtags vorschlagen lassen — zaehlt gegen das Kontingent, speichert nichts. */
  socialTextVorschlag(anfrage: HubTextVorschlagAnfrage): Promise<HubTextVorschlag> {
    return this.post("/social/text-vorschlag", anfrage).then(parse<HubTextVorschlag>);
  }

  /** Eine Aktion: eigener Zeitraum, eigener Fokus. Der Planer fuellt sie im Hintergrund. */
  socialAktionAnlegen(aktion: HubAktionNeu): Promise<HubSocialPlan> {
    return this.post("/social/aktionen", aktion).then(parse<HubSocialPlan>);
  }

  socialAktionLoeschen(planId: number): Promise<void> {
    return this.send(`/social/aktionen/${planId}`, "DELETE").then(parse<void>);
  }

  // -- Konten bei Meta (ab 0.16.0) --------------------------

  socialKonten(): Promise<HubSocialKonto[]> {
    return this.req("/social/konten").then(parse<HubSocialKonto[]>);
  }

  /** Startet den Login bei Meta. `zurueck`: wohin der Browser danach soll — die eigene Seite. */
  socialKontoVerbinden(platform: string, zurueck: string, by = ""): Promise<{ url: string }> {
    return this.post(`/social/konten/${platform}/verbinden`, { zurueck, by }).then(parse<{ url: string }>);
  }

  /** Was das TikTok-Konto gerade darf — TikTok verlangt, dass der Nutzer das vor dem Beitrag sieht. */
  socialTikTokCreator(): Promise<HubTikTokCreator> {
    return this.req("/social/konten/tiktok/creator").then(parse<HubTikTokCreator>);
  }

  socialKontoSeite(pageId: string): Promise<HubSocialKonto> {
    return this.post("/social/konten/facebook/seite", { page_id: pageId }).then(parse<HubSocialKonto>);
  }

  socialKontoAendern(platform: string, autoPost: boolean): Promise<HubSocialKonto> {
    return this.send(`/social/konten/${platform}`, "PATCH", { auto_post: autoPost }).then(parse<HubSocialKonto>);
  }

  socialKontoTrennen(platform: string): Promise<void> {
    return this.send(`/social/konten/${platform}`, "DELETE").then(parse<void>);
  }

  // -- Kommentare (ab 0.17.0) -------------------------------

  socialKommentare(): Promise<HubKommentare> {
    return this.req("/social/kommentare").then(parse<HubKommentare>);
  }

  socialKommentareAbrufen(): Promise<void> {
    return this.post("/social/kommentare/abrufen").then(parse<unknown>).then(() => undefined);
  }

  /** Oeffentlich antworten. Bei medizinnahen Sites vorher geprueft (409 `hwg` mit Befunden). */
  socialKommentarAntworten(id: number, text: string, by = ""): Promise<{ ok: boolean; id: string }> {
    return this.post(`/social/kommentare/${id}/antworten`, { text, by }).then(parse<{ ok: boolean; id: string }>);
  }

  socialKommentarAusblenden(id: number, hidden = true): Promise<void> {
    return this.post(`/social/kommentare/${id}/ausblenden`, { hidden }).then(parse<unknown>).then(() => undefined);
  }

  /** Leere Liste: alle neuen. */
  socialKommentareGelesen(ids: number[] = []): Promise<{ gelesen: number }> {
    return this.post("/social/kommentare/gelesen", { ids }).then(parse<{ gelesen: number }>);
  }

  socialKommentarVorschlag(id: number): Promise<{ text: string; verbraucht: number; kontingent: number | null }> {
    return this.post(`/social/kommentare/${id}/vorschlag`)
      .then(parse<{ text: string; verbraucht: number; kontingent: number | null }>);
  }

  /** Einen gescheiterten Beitrag erneut einreihen. `trotzdem` nach einem unklaren Ausgang bei Facebook. */
  socialPostErneut(id: number, trotzdem = false): Promise<HubSocialPost> {
    return this.post(`/social/posts/${id}/erneut`, { trotzdem }).then(parse<HubSocialPost>);
  }

  /** Von Hand gepostet — mit Link zum Beitrag. */
  socialPostGepostet(id: number, url = "", by = ""): Promise<HubSocialPost> {
    return this.post(`/social/posts/${id}/gepostet`, { url, by }).then(parse<HubSocialPost>);
  }

  // -- Newsletter (ab 0.7.0) --------------------------------

  /** Die Newsletter-Themen dieser Site. 403, wenn der Newsletter nicht gebucht ist. */
  newsletterThemen(): Promise<HubNewsletterThema[]> {
    return this.req("/newsletter/themen").then(parse<HubNewsletterThema[]>);
  }

  newsletterThemaAnlegen(thema: HubNewsletterThemaIn & { name: string }): Promise<HubNewsletterThema> {
    return this.post("/newsletter/themen", thema).then(parse<HubNewsletterThema>);
  }

  newsletterThemaAendern(id: number, patch: HubNewsletterThemaIn): Promise<HubNewsletterThema> {
    return this.send(`/newsletter/themen/${id}`, "PATCH", patch).then(parse<HubNewsletterThema>);
  }

  /** Archivieren statt loeschen — die Website behaelt die Abonnenten des Themas. */
  async newsletterThemaArchivieren(id: number): Promise<void> {
    const res = await this.send(`/newsletter/themen/${id}`, "DELETE");
    if (!res.ok) await parse<unknown>(res);
  }

  /** Die letzten Ausgaben — zur Freigabe, geplant und Archiv. */
  newsletterAusgaben(): Promise<HubNewsletterAusgabe[]> {
    return this.req("/newsletter/ausgaben").then(parse<HubNewsletterAusgabe[]>);
  }

  /** Betreff, Vorschautext oder Bausteine — nur solange die Ausgabe zur Freigabe steht. */
  newsletterAusgabeAendern(id: number, patch: HubNewsletterAusgabeAendern): Promise<HubNewsletterAusgabe> {
    return this.send(`/newsletter/ausgaben/${id}`, "PATCH", patch).then(parse<HubNewsletterAusgabe>);
  }

  /** Freigeben, Aenderungen anmerken (mit `feedback`), verwerfen oder zurueckziehen. */
  newsletterEntscheiden(id: number, aktion: HubNewsletterAktion, by = "", feedback = ""): Promise<HubNewsletterAusgabe> {
    return this.post(`/newsletter/ausgaben/${id}/${aktion}`, { by, feedback }).then(parse<HubNewsletterAusgabe>);
  }
  // -- Newsletter der Website (ab 0.8.0): bleibt auf der Website, geht nicht zur Engine --

  /** Versandstand und Abonnentenzahlen. 404, wenn die Website nicht selbst versendet. */
  newsletterLokal(): Promise<HubNewsletterLokal> {
    return this.req("/lokal/newsletter").then(parse<HubNewsletterLokal>);
  }

  newsletterAbonnenten(q = "", offset = 0): Promise<{ total: number; items: HubNewsletterAbonnent[] }> {
    const p = new URLSearchParams({ q, offset: String(offset) });
    return this.req(`/lokal/newsletter/abonnenten?${p}`).then(parse<{ total: number; items: HubNewsletterAbonnent[] }>);
  }

  /** Abonnent samt Protokoll loeschen (Loeschwunsch nach DSGVO). */
  async newsletterAbonnentLoeschen(id: number): Promise<void> {
    const res = await this.send(`/lokal/newsletter/abonnenten/${id}`, "DELETE");
    await parse<unknown>(res);
  }

  /** Ausgabe im Design der Website rendern. */
  newsletterVorschau(entwurf: HubNewsletterEntwurf): Promise<{ html: string; text: string }> {
    return this.post("/lokal/newsletter/vorschau", entwurf).then(parse<{ html: string; text: string }>);
  }

  /** Testmail an eine Adresse (Standard: angemeldete Person). */
  newsletterTestmail(entwurf: HubNewsletterEntwurf): Promise<{ ok: true; to: string }> {
    return this.post("/lokal/newsletter/test", entwurf).then(parse<{ ok: true; to: string }>);
  }

}

/** Server-seitig: spricht direkt mit der Engine, mit Site-Key. */
export class EngineClient {
  constructor(
    private readonly engineUrl: string,
    private readonly siteKey: string,
  ) {}

  async request(method: string, path: string, body?: unknown, signal?: AbortSignal): Promise<Response> {
    const url = `${this.engineUrl.replace(/\/$/, "")}/api/v1/hub${path}`;
    return fetch(url, {
      method,
      signal,
      headers: {
        "X-Site-Key": this.siteKey,
        ...(body === undefined ? {} : { "Content-Type": "application/json" }),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  }

  /**
   * Startmeldung. Sagt der Engine, wohin sie zum Veroeffentlichen rufen soll,
   * welche Paketversion laeuft und was der Sink kann.
   */
  async handshake(input: {
    packageVersion: string;
    hubEndpoint: string;
    capabilities: Record<string, unknown>;
  }): Promise<{ site_id: number; site_name: string; status: string; compatible: boolean; message: string }> {
    const res = await this.request("POST", "/handshake", {
      package_version: input.packageVersion,
      hub_endpoint: input.hubEndpoint,
      capabilities: input.capabilities,
    });
    return parse(res);
  }
}
